import { motion } from 'framer-motion';
import { Stethoscope, Inbox, Users } from 'lucide-react';

const products = [
  {
    id: 'aura-chart',
    name: 'Aura-Chart',
    platform: 'Chrome Extension',
    status: 'available',
    statusLabel: 'Early Access',
    tagline: 'Ambient clinical documentation',
    description:
      'Records your patient encounter and generates a complete, structured SOAP note typically within 30 seconds. Works over any web-based EMR on day one.',
    bullets: [
      '8 medical specialties + note templates',
      'OHIP billing codes',
      'English, French CA, Spanish, Hindi, Urdu, Punjabi, Arabic + auto-detect',
      '5 concurrent patient sessions',
      'Available on desktop (Chrome) today',
      'iOS & Android app',
    ],
    cta: { label: 'Install Free →', href: 'https://chromewebstore.google.com/detail/aura-chart/llkaecoefncjopopkbkhcmajliikeani', external: true },
    learnMore: { label: 'Learn more', href: '/solutions/aura-chart.html' },
    accentBg: 'bg-primary-600',
    accentText: 'text-primary-600',
    accentLight: 'bg-primary-50',
    borderActive: 'border-primary-200',
    icon: <Stethoscope className="h-7 w-7" />,
  },
  {
    id: 'aura-triage',
    name: 'Aura-Triage',
    platform: 'Web Platform',
    status: 'in-pilot',
    statusLabel: 'In Pilot',
    tagline: 'Fax inbox, sorted by urgency',
    description:
      'Classifies every incoming fax into Referrals, Prescriptions, Lab Reports, or Other — writes summaries, matches patients in Juno EMR, flags abnormal lab results, and creates task assignments sorted by urgency.',
    bullets: [
      'All 4 fax types sorted with per-category unread counts',
      '5-level RAS acuity scale (Critical → Administrative)',
      'Juno EMR matching + pre-filled patient registration',
      'AI task queue with P0–P3 priority + role assignee',
    ],
    cta: { label: 'Learn More →', href: '/solutions/aura-triage.html', external: false },
    learnMore: null,
    accentBg: 'bg-violet-600',
    accentText: 'text-violet-600',
    accentLight: 'bg-violet-50',
    borderActive: 'border-violet-200',
    icon: <Inbox className="h-7 w-7" />,
  },
  {
    id: 'consulting',
    name: 'Individual Clinic Consulting',
    platform: 'Professional Services',
    tagline: 'Hands-on workflow, IT & web support',
    description:
      "Every clinic runs differently. We sit down with your team, figure out what's taking the most time, and build something that fixes it — whether that's your EMR setup, your fax workflow, your patient intake forms, or your clinic website.",
    bullets: [
      'Workflow & automation audits',
      'EMR, faxing & contact-form automation',
      'Clinic website design & development',
      'Remote IT support & troubleshooting',
    ],
    cta: { label: 'Get in Touch →', href: 'mailto:contact@pulsestack.ai?subject=Clinic Consulting Inquiry', external: true },
    learnMore: null,
    accentBg: 'bg-teal-600',
    accentText: 'text-teal-600',
    accentLight: 'bg-teal-50',
    borderActive: 'border-teal-200',
    icon: <Users className="h-7 w-7" />,
  },
];

export default function ProductsShowcase() {
  return (
    <section id="products" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">Who we are</p>
          <h2 className="mt-3 section-heading">PulseStack AI builds clinical AI for Canadian healthcare</h2>
          <p className="section-sub">
            Canadian clinics spend more time on paperwork than on patients. We're here to fix that.
          </p>
        </motion.div>

        <motion.div
          className="text-center max-w-2xl mx-auto mt-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <p className="section-label">Our Products</p>
          <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
            Tools built around how your clinic actually works
          </h3>
          <p className="section-sub">
            One less thing to deal with at the end of a long clinic day.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product, i) => {
            const isComingSoon = product.status === 'coming-soon';
            return (
              <motion.div
                key={product.id}
                className={`relative flex flex-col rounded-2xl border bg-white p-7 transition-all duration-200 ${
                  isComingSoon
                    ? 'border-gray-200 opacity-80'
                    : `${product.borderActive} hover:shadow-lg`
                }`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
              >
                {/* Coming Soon ribbon */}
                {isComingSoon && (
                  <div className="absolute top-4 right-4 bg-gray-100 text-gray-500 text-xs font-semibold px-2.5 py-1 rounded-full">
                    Coming Soon
                  </div>
                )}

                {/* Icon + status */}
                <div className="flex items-start justify-between mb-5">
                  <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${product.accentLight} ${product.accentText}`}>
                    {product.icon}
                  </div>
                  {!isComingSoon && product.statusLabel && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      {product.statusLabel}
                    </span>
                  )}
                </div>

                {/* Name & platform */}
                <p className={`text-xs font-semibold uppercase tracking-widest ${isComingSoon ? 'text-gray-400' : product.accentText} mb-1`}>
                  {product.platform}
                </p>
                <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                <p className={`text-sm font-medium mt-0.5 ${isComingSoon ? 'text-gray-400' : product.accentText}`}>
                  {product.tagline}
                </p>

                {/* Description */}
                <p className="mt-4 text-sm text-gray-500 leading-relaxed flex-1">
                  {product.description}
                </p>

                {/* Bullets */}
                <ul className="mt-5 space-y-2">
                  {product.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg
                        className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isComingSoon ? 'text-gray-300' : product.accentText}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {b}
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="mt-7 flex items-center gap-3">
                  <a
                    href={product.cta.href}
                    {...(product.cta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className={`flex-1 text-center py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors min-h-[42px] flex items-center justify-center ${
                      isComingSoon
                        ? 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        : `${product.accentBg} text-white hover:opacity-90`
                    }`}
                  >
                    {product.cta.label}
                  </a>
                  {product.learnMore && (
                    <a
                      href={product.learnMore.href}
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
                    >
                      {product.learnMore.label}
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
